import Link from "next/link";
import { Box } from "@otrium/core";
import styled from "@emotion/styled";
import { Button } from "src/atoms/Button";
import { StyledCarousels } from "src/organisms/ProductView/ProductView.styled";

const CartHeaderNav = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.space8};
  padding: ${({ theme }) => theme.space.space8} 0;

  @media (max-width: ${({ theme }) => theme.breakpoints?.md}) {
    display: none;
  }
`;

const HomeLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.tone.black};
`;

const BackToShoppingLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.space8};
  color: ${({ theme }) => theme.colors.tone.black};
  font-size: ${({ theme }) => theme.fontSizes.fontSize14};
  line-height: 20px;
`;

const CartOrderSummaryContent = styled(Box)<{
  isDisabled?: boolean;
}>`
  position: relative;
  color: ${({ theme }) => theme.colors.tone.black};
  background-color: ${({ theme }) => theme.colors.tone.sand};

  ${({ isDisabled, theme }) =>
    isDisabled &&
    `
  &:before {
    width: 100%;
    height: 100%;
    content: '';
    position: absolute;
    bottom: 0;
    right: 0;
    top: 0;
    left: 0;
    z-index: 2;
    opacity: 0.67;
    background-color: ${theme.colors.tone.sand};
  }
  `}

  .free-shipping {
    font-size ${({ theme }) => theme.fontSizes.fontSize14};
    font-weight: 600;
    font-style: normal;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.semantic.green};
  }
`;

const PaymentMethodsWrapper = styled.div`
  padding: ${({ theme }) => theme.space.space16};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.space.space32} 0;
  }
`;

const CartHeading = styled.h1<{ isEmpty?: boolean }>`
  margin-top: ${({ theme }) => theme.space.space16};
  margin-bottom: ${({ isEmpty, theme }) =>
    isEmpty ? theme.space.space8 : theme.space.space16};
  color: ${({ theme }) => theme.colors.tone.black};
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.fontSize18};
  font-weight: bold;
  line-height: 24px;
  text-align: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding-top: 10px;
    padding-bottom: 10px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    margin-top: ${({ theme }) => theme.space.space24};
    margin-bottom: ${({ isEmpty, theme }) =>
      isEmpty ? theme.space.space8 : theme.space.space24};
    font-size: ${({ theme }) => theme.fontSizes.fontSize24};
    line-height: 32px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    text-align: ${({ isEmpty }) => (isEmpty ? "center" : "left")};
  }
`;

const StickyCheckoutBar = styled.div`
  display: none;
  position: sticky;
  bottom: 0;
  z-index: ${({ theme }) => theme.zIndex.header - 1};
  background-color: ${({ theme }) => theme.colors.tone.white};
  box-shadow: 0 -4px 8px 0 ${({ theme }) => theme.effects.overlay.black_10};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: block;
  }

  [data-variant="mobile"]:not([data-reached="true"]) + [data-checkout-action] {
    padding-top: ${({ theme }) => theme.space.space8};
  }
`;

const StickyCheckoutBarAction = styled.div`
  padding: ${({ theme }) => theme.space.space16};
`;

const EmptyCartInlineCta = styled.div`
  display: block;
  margin-top: ${({ theme }) => theme.space.space16};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: none;
  }
`;

const EmptyCartStickyCta = styled.div`
  display: none;
  position: sticky;
  bottom: 0;
  padding: ${({ theme }) => theme.space.space16};
  background-color: ${({ theme }) => theme.colors.tone.white};
  box-shadow: 0 -4px 8px 0 ${({ theme }) => theme.effects.overlay.black_10};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: block;
  }
`;

const CartItemsWrapper = styled(Box)`
  grid-area: cart-items;
`;

const CartViewOrderSummaryWrapper = styled(Box)`
  grid-area: summary;
`;

const SummaryWrapper = styled.div`
  position: sticky;
  top: 100px;
  height: fit-content;
`;

const CartViewCarouselsWrapper = styled(Box)`
  margin-top: ${({ theme }) => theme.space[2]}px;
  margin-bottom: ${({ theme }) => theme.space[2]}px;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    margin-top: ${({ theme }) => theme.space[5]}px;
    margin-bottom: ${({ theme }) => theme.space[6]}px;
  }
`;

const StyledSmallButton = styled(Button)`
  padding: ${({ theme }) => theme.space[2]}px ${({ theme }) => theme.space[4]}px;
  height: 40px;
`;

const CarouselWrapper = styled.div<{ isEmptyCart?: boolean }>`
  grid-area: carousel;

  margin-bottom: 96px;
  min-width: 0;
  @media (max-width: ${({ theme }) => theme.breakpoints[2]}) {
    margin-bottom: ${({ theme }) => theme.space.space48};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    margin-bottom: ${({ isEmptyCart, theme }) =>
      isEmptyCart ? theme.space.space24 : "0"};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-column: span 2;
  }
`;

const CartCarousel = styled(StyledCarousels)<{ isEmptyCart?: boolean }>`
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    margin-top: ${({ isEmptyCart, theme }) =>
      isEmptyCart ? theme.space.space24 : "0"};

    h3 {
      font-size: ${({ theme }) => theme.fontSizes.fontSize14};
      line-height: 20px;
    }
  }
`;

export {
  BackToShoppingLink,
  CartHeaderNav,
  CartHeading,
  HomeLink,
  StickyCheckoutBar,
  StickyCheckoutBarAction,
  EmptyCartInlineCta,
  EmptyCartStickyCta,
  CartItemsWrapper,
  CartViewOrderSummaryWrapper,
  CartViewCarouselsWrapper,
  CartOrderSummaryContent,
  PaymentMethodsWrapper,
  StyledSmallButton,
  SummaryWrapper,
  CartCarousel,
  CarouselWrapper,
};
